import { useCallback, useEffect, useMemo, useRef, useState, type RefObject, type TouchEvent } from "react";
import { useQuery } from "convex/react";
import { motion, useReducedMotion } from "motion/react";
import { Calendar, ChevronLeft, ChevronRight, Info, LineChart, X } from "lucide-react";

import { AppIcon } from "../../components/apps/AppIcon";
import { FocusSeriesChart, type FocusSeriesPoint } from "../../components/screen0-ring/FocusSeriesChart";
import type { RingMetric } from "../../components/screen0-ring/Screen0Ring";
import { useFriendlyAppName } from "../../lib/apps/useFriendlyAppName";
import { queryRef } from "../../lib/convex/refs";
import {
  canNavigateCalendarMonthNext,
  canNavigateCalendarMonthPrev,
  isSameMonth,
  parseDateKey,
  startOfMonth,
} from "../../lib/dateNav";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { useMarketingDemoOverlay } from "../../lib/marketing/MarketingDemoProvider";
import { todayKey } from "../../lib/format";
import {
  WEEKDAY_LABELS,
  addMonths,
  buildMonthGrid,
  buildWeekGrid,
  dayValueForEntity,
  entityValuesByDate,
  firstOfMonth,
  formatHrMin,
  formatOpens,
  intensityScale,
  isViewableMonth,
  monthRange,
  type EntityRef,
  type HeatmapResult,
  type Metric,
} from "../../lib/habits/heatmap";
import { appCategoryDisplayLabel, localizedCategoryLabel } from "../../lib/apps/categoryIcons";
import { resolveCanonicalCategoryKey } from "../../lib/apps/categoryNormalization";
import { isUnattributedApp } from "../../lib/native/mergeSameDaySnapshot";
import { CalendarVerticalScroller } from "../calendar/CalendarVerticalScroller";
import {
  buildInitialWeekStarts,
  buildWeekDays,
  ensureWeekStartsCoverMonth,
  heatmapRangeForWeekStarts,
  prependWeekCount,
} from "../today/calendarScrollLogic";
import { BottomSheet } from "./BottomSheet";
import { HeatmapMatrix } from "./HeatmapMatrix";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";

const heatmapRef = queryRef<
  { anonymousDeviceId: string; startDate: string; endDate: string },
  HeatmapResult
>("usageHeatmap:getHeatmap");

const habitHourlyProfileRef = queryRef<
  {
    anonymousDeviceId: string;
    appIdentifier?: string;
    displayName: string;
    isCategory?: boolean;
    startDate: string;
    endDate: string;
  },
  { points: FocusSeriesPoint[]; hasOpens: boolean; daysWithData: number }
>("usage:getAppHabitHourlyProfile");

export type CalendarScrollMode = "vertical" | "static";
type PanelMode = "calendar" | "chart";

const HABITS_DETAIL_APP_ICON_SIZE = 21;
const HABITS_DETAIL_APP_ICON_RENDER_SIZE = 27;

type AppDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  appIdentifier?: string;
  displayName: string;
  category?: string;
  color: string;
  initialDate?: string;
  /** Explicit override from HeatmapEntity.isCategory — covers edge cases like "Uncategorized". */
  isCategory?: boolean;
  /** Vertical week scroll (Habits) vs single static month grid (Today). */
  calendarScrollMode?: CalendarScrollMode;
  /** Habits header metric — used by the chart panel in vertical mode only. */
  habitsMetric?: Metric;
};

function formatFullDate(dateKey: string): string {
  return parseDateKey(dateKey).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatMonthLabel(monthAnchor: string): string {
  return parseDateKey(monthAnchor).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function AppDetailDrawer({
  open,
  onClose,
  appIdentifier,
  displayName,
  category,
  color,
  initialDate,
  isCategory: isCategoryProp,
  calendarScrollMode = "static",
  habitsMetric = "time",
}: AppDetailDrawerProps) {
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const anonymousDeviceId = useDeviceId();
  const referenceDate = todayKey();
  const openDate = initialDate ?? referenceDate;
  const isVerticalScroll = calendarScrollMode === "vertical";
  const ringMetric: RingMetric = habitsMetric;

  const isCategory = isCategoryProp ?? (!appIdentifier && !!category && displayName === category);
  const entity = useMemo<EntityRef>(
    () => ({ appIdentifier, displayName, category, isCategory }),
    [appIdentifier, displayName, category, isCategory],
  );
  const appStoreName = useFriendlyAppName({ displayName, appIdentifier, category });
  const headerName = isCategory
    ? localizedCategoryLabel(resolveCanonicalCategoryKey(category ?? displayName), t)
    : isUnattributedApp({ appIdentifier, displayName })
      ? t.categories.other
      : appStoreName;

  const grid = useMemo(() => buildWeekGrid(referenceDate), [referenceDate]);

  const [selectedDate, setSelectedDate] = useState(openDate);
  const [monthAnchor, setMonthAnchor] = useState(() => firstOfMonth(openDate));
  const [visibleMonth, setVisibleMonth] = useState(() => firstOfMonth(openDate));
  const [weekStarts, setWeekStarts] = useState<string[]>(() =>
    buildInitialWeekStarts(openDate, referenceDate),
  );
  const [scrollRequest, setScrollRequest] = useState<{ month: string; nonce: number } | null>(null);
  const [panelMode, setPanelMode] = useState<PanelMode>("calendar");

  useEffect(() => {
    if (!open) return;
    setPanelMode("calendar");
    setSelectedDate(openDate);
    const month = firstOfMonth(openDate);
    setMonthAnchor(month);
    if (isVerticalScroll) {
      setVisibleMonth(month);
      setWeekStarts(buildInitialWeekStarts(openDate, referenceDate));
      setScrollRequest({ month, nonce: Date.now() });
    }
  }, [open, openDate, isVerticalScroll, referenceDate]);

  const miniDataReal = useQuery(
    heatmapRef,
    open ? { anonymousDeviceId, startDate: grid.startDate, endDate: grid.endDate } : "skip",
  );
  const miniData = useMarketingDemoOverlay(
    "usageHeatmap:getHeatmap",
    open ? { anonymousDeviceId, startDate: grid.startDate, endDate: grid.endDate } : "skip",
    miniDataReal,
  );

  const staticMonthRange = monthRange(monthAnchor, referenceDate);
  const scrollHeatmapRange = useMemo(
    () => heatmapRangeForWeekStarts(weekStarts, referenceDate),
    [referenceDate, weekStarts],
  );
  const calendarQueryRange = isVerticalScroll ? scrollHeatmapRange : staticMonthRange;

  const showCalendarPanel = !isVerticalScroll || panelMode === "calendar";

  const monthDataReal = useQuery(
    heatmapRef,
    open && showCalendarPanel ? { anonymousDeviceId, ...calendarQueryRange } : "skip",
  );
  const monthData = useMarketingDemoOverlay(
    "usageHeatmap:getHeatmap",
    open && showCalendarPanel ? { anonymousDeviceId, ...calendarQueryRange } : "skip",
    monthDataReal,
  );

  const habitProfileArgs = open && isVerticalScroll && panelMode === "chart"
    ? {
        anonymousDeviceId,
        appIdentifier: isCategory ? undefined : appIdentifier,
        displayName,
        isCategory: isCategory || undefined,
        startDate: grid.startDate,
        endDate: grid.endDate,
      }
    : "skip";
  const habitProfileReal = useQuery(habitHourlyProfileRef, habitProfileArgs);
  const habitProfile = useMarketingDemoOverlay(
    "usage:getAppHabitHourlyProfile",
    habitProfileArgs,
    habitProfileReal,
  );

  const miniByDate = useMemo(
    () => entityValuesByDate(miniData?.days ?? [], entity, "time"),
    [miniData, entity],
  );
  const miniScale = useMemo(
    () => intensityScale(grid.allDates.map((date) => miniByDate.get(date) ?? 0)),
    [grid, miniByDate],
  );

  const monthByDate = useMemo(() => {
    const map = new Map<string, { seconds: number; opens: number }>();
    for (const day of monthData?.days ?? []) {
      map.set(day.date, dayValueForEntity(day.apps, entity));
    }
    return map;
  }, [monthData, entity]);

  const { weeks, monthLabel: staticMonthLabel } = useMemo(
    () => buildMonthGrid(monthAnchor, referenceDate),
    [monthAnchor, referenceDate],
  );

  const monthLabel = isVerticalScroll ? formatMonthLabel(visibleMonth) : staticMonthLabel;
  const canGoNextMonth = isVerticalScroll
    ? canNavigateCalendarMonthNext(visibleMonth, referenceDate)
    : isViewableMonth(addMonths(monthAnchor, 1), referenceDate);
  const canGoPrevMonth = isVerticalScroll
    ? canNavigateCalendarMonthPrev(visibleMonth)
    : true;

  const touchStartX = useRef<number | null>(null);

  const handleMiniCellClick = useCallback(
    (date: string) => {
      setSelectedDate(date);
      const month = firstOfMonth(date);
      if (isVerticalScroll) {
        setVisibleMonth(month);
        setWeekStarts((current) => ensureWeekStartsCoverMonth(current, month, referenceDate));
        setScrollRequest({ month, nonce: Date.now() });
      } else {
        setMonthAnchor(month);
      }
    },
    [isVerticalScroll, referenceDate],
  );

  const goMonthPrev = useCallback(() => {
    if (!canGoPrevMonth) return;
    if (isVerticalScroll) {
      const month = startOfMonth(addMonths(visibleMonth, -1));
      setWeekStarts((current) => ensureWeekStartsCoverMonth(current, month, referenceDate));
      setVisibleMonth(month);
      setScrollRequest({ month, nonce: Date.now() });
    } else {
      setMonthAnchor(addMonths(monthAnchor, -1));
    }
  }, [canGoPrevMonth, isVerticalScroll, monthAnchor, referenceDate, visibleMonth]);

  const goMonthNext = useCallback(() => {
    if (!canGoNextMonth) return;
    if (isVerticalScroll) {
      const month = startOfMonth(addMonths(visibleMonth, 1));
      setWeekStarts((current) => ensureWeekStartsCoverMonth(current, month, referenceDate));
      setVisibleMonth(month);
      setScrollRequest({ month, nonce: Date.now() });
    } else {
      setMonthAnchor(addMonths(monthAnchor, 1));
    }
  }, [canGoNextMonth, isVerticalScroll, monthAnchor, referenceDate, visibleMonth]);

  const handleVisibleMonthChange = useCallback((month: string) => {
    setVisibleMonth((current) => (current === month ? current : month));
  }, []);

  const handlePrependWeeks = useCallback((count: number) => {
    setWeekStarts((current) => {
      if (current.length === 0) return current;
      return [...prependWeekCount(current[0]!, count), ...current];
    });
  }, []);

  const selected = monthByDate.get(selectedDate);
  const hasSelectedUsage = !!selected && (selected.seconds > 0 || selected.opens > 0);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      labelledBy="app-detail-title"
      maxHeight={isVerticalScroll ? "92svh" : undefined}
    >
      <div className="px-4 pt-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <AppIcon
              displayName={displayName}
              appIdentifier={appIdentifier}
              category={category}
              isCategory={isCategory}
              color={color}
              size={HABITS_DETAIL_APP_ICON_SIZE}
              iconSize={HABITS_DETAIL_APP_ICON_RENDER_SIZE}
            />
            <div className="min-w-0">
              <h2
                id="app-detail-title"
                className="truncate text-lg font-semibold text-[oklch(92%_0.02_150)]"
              >
                {headerName}
              </h2>
              {!isCategory ? (
                <p className="truncate text-xs text-[oklch(72%_0.025_155)]">
                  {appCategoryDisplayLabel(category, t, appIdentifier)}
                </p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="icon-button"
            style={{ minWidth: 36, minHeight: 36, border: "none" }}
            aria-label={t.common.close}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div
        className={`overflow-y-auto px-4 pb-2 ${isVerticalScroll ? "max-h-[78svh]" : "max-h-[64svh]"}`}
      >
        <div className="mb-5 rounded-xl bg-[oklch(16%_0.015_165)] p-3">
          <HeatmapMatrix
            columns={grid.columns}
            valueForDate={(date) => miniByDate.get(date) ?? 0}
            scale={miniScale}
            color={color}
            cellSize={9}
            gap={2}
            highlightDate={selectedDate}
            onCellClick={handleMiniCellClick}
          />
        </div>

        {isVerticalScroll ? (
          <div className="mb-3 flex justify-center">
            <HabitsPanelModeToggle mode={panelMode} onChange={setPanelMode} t={t} />
          </div>
        ) : null}

        <div
          className="rounded-xl bg-[oklch(16%_0.015_165)] p-3"
          {...(!isVerticalScroll
            ? {
                onTouchStart: (e: TouchEvent) => {
                  touchStartX.current = e.touches[0].clientX;
                },
                onTouchEnd: (e: TouchEvent) => {
                  if (touchStartX.current === null) return;
                  const delta = e.changedTouches[0].clientX - touchStartX.current;
                  touchStartX.current = null;
                  if (delta > 50) goMonthPrev();
                  else if (delta < -50 && canGoNextMonth) goMonthNext();
                },
              }
            : {})}
        >
          {isVerticalScroll && panelMode === "chart" ? (
            <HabitsRhythmChart
              profile={habitProfile}
              metric={ringMetric}
              color={color}
              t={t}
            />
          ) : (
            <>
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={goMonthPrev}
              disabled={!canGoPrevMonth}
              className="icon-button"
              style={{ minWidth: 34, minHeight: 34 }}
              aria-label={t.habits.previousMonth}
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-sm font-semibold text-[oklch(88%_0.02_150)]">{monthLabel}</span>
            <button
              type="button"
              onClick={goMonthNext}
              disabled={!canGoNextMonth}
              className="icon-button"
              style={{ minWidth: 34, minHeight: 34 }}
              aria-label={t.habits.nextMonth}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 pb-1">
            {WEEKDAY_LABELS.map((label, index) => (
              <div key={index} className="text-center text-[10px] text-[oklch(52%_0.02_155)]">
                {label}
              </div>
            ))}
          </div>

          {isVerticalScroll ? (
            <CalendarVerticalScroller
              weekStarts={weekStarts}
              visibleMonth={visibleMonth}
              scrollRequest={scrollRequest}
              selectedDate={selectedDate}
              onVisibleMonthChange={handleVisibleMonthChange}
              onPrependWeeks={handlePrependWeeks}
              renderWeekRow={({ weekStart, rowMeasureRef, visibleMonth: rowVisibleMonth }) => (
                <HabitsCalendarWeekRow
                  weekStart={weekStart}
                  rowMeasureRef={rowMeasureRef}
                  visibleMonth={rowVisibleMonth}
                  selectedDate={selectedDate}
                  referenceDate={referenceDate}
                  color={color}
                  monthByDate={monthByDate}
                  onSelectDate={setSelectedDate}
                />
              )}
            />
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {weeks.flatMap((week) =>
                week.map((cell) => (
                  <HabitsDayCell
                    key={cell.date}
                    date={cell.date}
                    inMonth={cell.inMonth}
                    inFuture={cell.inFuture}
                    isSelected={cell.date === selectedDate}
                    color={color}
                    value={monthByDate.get(cell.date)}
                    onSelect={setSelectedDate}
                  />
                )),
              )}
            </div>
          )}

          <div className="liquid-glass-divider mt-3 pt-3">
            <p className="text-xs text-[oklch(58%_0.025_155)]">{formatFullDate(selectedDate)}</p>
            <p className="mt-0.5 text-base font-semibold text-[oklch(90%_0.02_150)]">
              {hasSelectedUsage
                ? `${formatHrMin(selected!.seconds, languageCode)} · ${formatOpens(selected!.opens)}`
                : t.appDetail.noUsage}
            </p>
          </div>
            </>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}

type HabitsRhythmChartProps = {
  profile:
    | { points: FocusSeriesPoint[]; hasOpens: boolean; daysWithData: number }
    | undefined;
  metric: RingMetric;
  color: string;
  t: ReturnType<typeof getTranslations>;
};

function HabitsRhythmChart({ profile, metric, color, t }: HabitsRhythmChartProps) {
  const [infoOpen, setInfoOpen] = useState(false);
  const metricLabel = metric === "opens" ? t.ring.activations : t.ring.screenTime;
  const infoBody =
    metric === "opens"
      ? [t.appDetail.dailyAverageInfoBodyOpens1, t.appDetail.dailyAverageInfoBodyOpens2]
      : [t.appDetail.dailyAverageInfoBodyTime1, t.appDetail.dailyAverageInfoBodyTime2];

  return (
    <div className="grid gap-2">
      <div className="relative w-fit max-w-full pr-3.5">
        <h3 className="text-sm font-semibold text-[oklch(88%_0.02_150)]">{t.appDetail.dailyAverage}</h3>
        <button
          type="button"
          onClick={() => setInfoOpen(true)}
          aria-label={formatTemplate(t.appDetail.dailyAverageInfoAria, { metric: metricLabel })}
          className="liquid-glass-icon-ring absolute right-0 top-0 inline-flex size-[13px] items-center justify-center text-[8px] font-bold leading-none text-[oklch(58%_0.03_155)] transition-colors duration-150 hover:text-[oklch(72%_0.03_155)] active:scale-95"
        >
          <Info size={8} strokeWidth={2.5} aria-hidden />
        </button>
      </div>
      {profile === undefined ? (
        <div
          className="w-full animate-pulse rounded-md"
          style={{ height: 150, background: "oklch(24% 0.02 165)" }}
          aria-hidden
        />
      ) : (
        <>
          <FocusSeriesChart
            key={metric}
            points={profile.points}
            mode="day"
            metric={metric}
            color={color}
            hasOpens={profile.hasOpens}
            averagedActivations
          />
          {!profile.hasOpens ? (
            <p className="px-1 text-[11px] leading-4" style={{ color: "oklch(52% 0.016 155)" }}>
              {t.appDetail.activationsNotTrackedPeriod}
            </p>
          ) : null}
        </>
      )}
      <BottomSheet open={infoOpen} onClose={() => setInfoOpen(false)} labelledBy="daily-average-info-title">
        <div className="grid gap-3 px-4 pt-4 pb-2">
          <h2 id="daily-average-info-title" className="text-lg font-semibold text-[oklch(92%_0.02_150)]">
            {t.appDetail.dailyAverage}
          </h2>
          <div className="grid gap-2.5">
            {infoBody.map((paragraph) => (
              <p key={paragraph} className="text-sm leading-6 text-[oklch(72%_0.025_155)]">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

function HabitsPanelModeToggle({
  mode,
  onChange,
  t,
}: {
  mode: PanelMode;
  onChange: (mode: PanelMode) => void;
  t: ReturnType<typeof getTranslations>;
}) {
  const prefersReducedMotion = useReducedMotion();
  const options: ReadonlyArray<{ value: PanelMode; label: string; icon: typeof Calendar }> = [
    { value: "calendar", label: t.appDetail.panelCalendar, icon: Calendar },
    { value: "chart", label: t.appDetail.panelChart, icon: LineChart },
  ];
  return (
    <div className="liquid-glass-segmented">
      {options.map((option) => {
        const active = mode === option.value;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={active}
            aria-label={option.label}
            className={`relative flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-semibold transition-colors ${active ? "text-[oklch(12%_0.015_160)]" : "text-[oklch(70%_0.025_155)]"}`}
          >
            {active ? (
              <motion.span
                layoutId="habits-panel-mode"
                className="absolute inset-0 rounded-md bg-[oklch(72%_0.09_155)]"
                transition={prefersReducedMotion ? { duration: 0 } : { type: "spring", stiffness: 480, damping: 38 }}
              />
            ) : null}
            <Icon size={14} className="relative" aria-hidden />
            <span className="relative">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

type HabitsCalendarWeekRowProps = {
  weekStart: string;
  rowMeasureRef?: RefObject<HTMLDivElement | null>;
  visibleMonth: string;
  selectedDate: string;
  referenceDate: string;
  color: string;
  monthByDate: Map<string, { seconds: number; opens: number }>;
  onSelectDate: (date: string) => void;
};

function HabitsCalendarWeekRow({
  weekStart,
  rowMeasureRef,
  visibleMonth,
  selectedDate,
  referenceDate,
  color,
  monthByDate,
  onSelectDate,
}: HabitsCalendarWeekRowProps) {
  const days = buildWeekDays(weekStart);

  return (
    <div ref={rowMeasureRef} className="grid grid-cols-7 gap-1">
      {days.map((date) => (
        <HabitsDayCell
          key={date}
          date={date}
          inMonth={isSameMonth(date, visibleMonth)}
          inFuture={date > referenceDate}
          isSelected={date === selectedDate}
          color={color}
          value={monthByDate.get(date)}
          onSelect={onSelectDate}
        />
      ))}
    </div>
  );
}

type HabitsDayCellProps = {
  date: string;
  inMonth: boolean;
  inFuture: boolean;
  isSelected: boolean;
  color: string;
  value?: { seconds: number; opens: number };
  onSelect: (date: string) => void;
};

function HabitsDayCell({
  date,
  inMonth,
  inFuture,
  isSelected,
  color,
  value,
  onSelect,
}: HabitsDayCellProps) {
  const hasData = !!value && (value.seconds > 0 || value.opens > 0);
  const disabled = inFuture || !inMonth;
  const dayNumber = parseDateKey(date).getDate();

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(date)}
      className="relative flex aspect-square items-center justify-center rounded-full text-[13px]"
      style={{
        backgroundColor: isSelected ? color : "transparent",
        color: isSelected
          ? "oklch(98% 0.01 150)"
          : !inMonth || inFuture
            ? "oklch(40% 0.015 155)"
            : "oklch(84% 0.02 150)",
        fontWeight: isSelected ? 600 : 400,
      }}
    >
      {dayNumber}
      {hasData && !isSelected ? (
        <span
          className="absolute bottom-[5px] h-1 w-1 rounded-full"
          style={{ backgroundColor: color }}
        />
      ) : null}
    </button>
  );
}
